import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.projects.ProjectView.';
  public static readonly discardChangesText = `${Id.prefix}discardChangesText`;
  public static readonly documentsTabTitle = `${Id.prefix}documentsTabTitle`;
  public static readonly reportsTabTitle = `${Id.prefix}reportsTabTitle`;
  public static readonly completeProjectConfirmation = `${Id.prefix}completeProjectConfirmation`;
  public static readonly cancelProjectButton = `${Id.prefix}cancelProjectButton`;
  public static readonly cancelProjectConfirmButton = `${Id.prefix}cancelProjectConfirmButton`;
  public static readonly cancelProjectMessage = `${Id.prefix}cancelProjectMessage`;
  public static readonly cancelProjectReasonLabel = `${Id.prefix}cancelProjectReasonLabel`;
  public static readonly completeProjectButton = `${Id.prefix}completeProjectButton`;
  public static readonly saveButton = `${Id.prefix}saveButton`;
  public static readonly statusButton = `${Id.prefix}statusButton`;
  public static readonly addQuoteButton = `${Id.prefix}addQuoteButton`;
  public static readonly detailsTabTitle = `${Id.prefix}detailsTabTitle`;
  public static readonly finalStateReasonDeadline = `${Id.prefix}finalStateReason.Deadline`;
  public static readonly finalStateReasonCompetitorWin = `${Id.prefix}finalStateReason.CompetitorWin`;
  public static readonly finalStateReasonUnresponsiveClient = `${Id.prefix}finalStateReason.UnresponsiveClient`;
  public static readonly finalStateReasonIndecisiveClient = `${Id.prefix}finalStateReason.IndecisiveClient`;
  public static readonly finalStateReasonUnhappyClient = `${Id.prefix}finalStateReason.UnhappyClient`;
  public static readonly finalStateReasonDifficultClient = `${Id.prefix}finalStateReason.DifficultClient`;
  public static readonly finalStateReasonSuperseded = `${Id.prefix}finalStateReason.Superseded`;
  public static readonly finalStateReasonOther = `${Id.prefix}finalStateReason.Other`;
}

export class LangProjectView {
  private constructor() {}

  public static discardChangesText(): string {
    return interpolate(Id.discardChangesText);
  }

  public static documentsTabTitle(): string {
    return interpolate(Id.documentsTabTitle);
  }

  public static reportsTabTitle(): string {
    return interpolate(Id.reportsTabTitle);
  }

  public static completeProjectConfirmation(): string {
    return interpolate(Id.completeProjectConfirmation);
  }

  public static cancelProjectButton(): string {
    return interpolate(Id.cancelProjectButton);
  }

  public static cancelProjectConfirmButton(): string {
    return interpolate(Id.cancelProjectConfirmButton);
  }

  public static cancelProjectMessage(): string {
    return interpolate(Id.cancelProjectMessage);
  }

  public static cancelProjectReasonLabel(): string {
    return interpolate(Id.cancelProjectReasonLabel);
  }

  public static completeProjectButton(): string {
    return interpolate(Id.completeProjectButton);
  }

  public static saveButton(): string {
    return interpolate(Id.saveButton);
  }

  public static statusButton(): string {
    return interpolate(Id.statusButton);
  }

  public static addQuoteButton(): string {
    return interpolate(Id.addQuoteButton);
  }

  public static detailsTabTitle(): string {
    return interpolate(Id.detailsTabTitle);
  }

  public static readonly finalStateReason = {
    deadline: () => interpolate(Id.finalStateReasonDeadline),
    competitorWin: () => interpolate(Id.finalStateReasonCompetitorWin),
    unresponsiveClient: () => interpolate(Id.finalStateReasonUnresponsiveClient),
    indecisiveClient: () => interpolate(Id.finalStateReasonIndecisiveClient),
    unhappyClient: () => interpolate(Id.finalStateReasonUnhappyClient),
    difficultClient: () => interpolate(Id.finalStateReasonDifficultClient),
    superseded: () => interpolate(Id.finalStateReasonSuperseded),
    other: () => interpolate(Id.finalStateReasonOther)
  };
}

export class ProjectViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // General UI
    register.add(Id.discardChangesText, 'Discard Changes', 'Button text for discarding changes');
    // Tabs
    register.add(Id.documentsTabTitle, 'Documents', 'Tab title for project documents/resources');
    register.add(Id.reportsTabTitle, 'Reports', 'Tab title for project reports');
    register.add(Id.detailsTabTitle, 'Details', 'Tab title for project details');
    // Confirmation and state change
    register.add(
      Id.completeProjectConfirmation,
      "Do you want to complete this {{glossary 'project'}}?",
      'Confirmation message for completing a project'
    );
    register.add(Id.cancelProjectButton, 'Cancel', 'Button text for cancelling a project');
    register.add(Id.cancelProjectConfirmButton, 'Cancel Project', 'Confirm button text for cancel project modal');
    register.add(
      Id.cancelProjectMessage,
      "Please select the reason for cancelling the {{glossary 'project'}}",
      'Message for cancelling a project'
    );
    register.add(Id.cancelProjectReasonLabel, 'Cancellation Reason', 'Label for cancellation reason');
    register.add(Id.completeProjectButton, 'Complete', 'Button text for completing a project');
    // Action buttons
    register.add(Id.saveButton, 'Save', 'Button text for saving');
    register.add(Id.statusButton, 'Status', 'Button text for status');
    register.add(Id.addQuoteButton, "Add {{glossary 'quote'}}", 'Button text for adding a quote');
    // Final state reasons
    register.add(Id.finalStateReasonDeadline, 'Deadline', 'Project final state reason: Deadline');
    register.add(Id.finalStateReasonCompetitorWin, 'Competitor Win', 'Project final state reason: Competitor Win');
    register.add(
      Id.finalStateReasonUnresponsiveClient,
      'Unresponsive Client',
      'Project final state reason: Unresponsive Client'
    );
    register.add(
      Id.finalStateReasonIndecisiveClient,
      'Indecisive Client',
      'Project final state reason: Indecisive Client'
    );
    register.add(Id.finalStateReasonUnhappyClient, 'Unhappy Client', 'Project final state reason: Unhappy Client');
    register.add(
      Id.finalStateReasonDifficultClient,
      'Difficult Client',
      'Project final state reason: Difficult Client'
    );
    register.add(Id.finalStateReasonSuperseded, 'Superseded', 'Project final state reason: Superseded');
    register.add(Id.finalStateReasonOther, 'Other', 'Project final state reason: Other');
  }
}
